import { useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useCompany } from '@/context/companies'
import { useInfiniteReadFiscalNoteList } from '@/api/accounting-dealer'
import {
  isChangingAccountantWithoutCertificate,
  resolveFiscalNoteBorrowerFilter,
} from '@/helpers'
import { MonthsEnum } from '@/types'
import { Alert, Box, Fab, Skeleton, Stack, Typography } from '@mui/material'
import { FiscalNoteAccordion } from './fiscal-note-accordion'
import { formatISO, lastDayOfMonth } from 'date-fns'
import { ErrorOutline } from '@mui/icons-material'
import { routes } from '@/router'
import { List, RowComponentProps, useDynamicRowHeight } from 'react-window'
import { FiscalNote } from '@/types'

const LOAD_THRESHOLD = 10
const COLLAPSED_ROW_HEIGHT = 56

type FiscalNotesListProps = {
  month: string
  year: string
  borrowerSearch?: string
}

type RowExtraProps = {
  items: FiscalNote[]
  expandedId: number | undefined
  onExpandChange: (
    id: number,
  ) => (event: React.SyntheticEvent, expanded: boolean) => void
  hasNextPage: boolean
}

type RowProps = RowComponentProps<RowExtraProps>

function FiscalNoteRow({
  index,
  style,
  items,
  expandedId,
  onExpandChange,
  hasNextPage,
}: RowProps) {
  const isLoadingRow = index >= items.length

  if (isLoadingRow && hasNextPage) {
    return (
      <div style={style}>
        <Skeleton variant="rounded" height={COLLAPSED_ROW_HEIGHT} />
      </div>
    )
  }

  const fiscalNote = items[index]

  if (!fiscalNote) return null

  return (
    <Box sx={{ ...style, pb: [1, 2] }}>
      <FiscalNoteAccordion
        fiscalNote={fiscalNote}
        expanded={expandedId === fiscalNote.id}
        onChange={onExpandChange(fiscalNote.id)}
      />
    </Box>
  )
}

export function FiscalNotesList({
  month,
  year,
  borrowerSearch,
}: FiscalNotesListProps) {
  const { company } = useCompany()

  const [fiscalNoteExpanded, setFiscalNoteExpanded] = useState<number>()

  const getStartDate = useCallback(() => {
    const monthIndex = Number(month) - 1
    return formatISO(new Date(Number(year), monthIndex, 1), {
      representation: 'date',
    })
  }, [month, year])

  const getEndDate = useCallback(() => {
    const monthIndex = Number(month) - 1
    const dateWithLastDayOfMonth = lastDayOfMonth(
      new Date(Number(year), monthIndex, 1),
    )
    return formatISO(dateWithLastDayOfMonth, { representation: 'date' })
  }, [month, year])

  const borrowerFilter = resolveFiscalNoteBorrowerFilter(borrowerSearch ?? '')

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteReadFiscalNoteList({
    company_key: company.key,
    start_date: getStartDate(),
    end_date: getEndDate(),
    ...borrowerFilter,
  })

  const rowHeight = useDynamicRowHeight({
    defaultRowHeight: COLLAPSED_ROW_HEIGHT + 8,
  })

  const hideIssueButton = isChangingAccountantWithoutCertificate(company)

  const certificateAlert = hideIssueButton ? (
    <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
      Para emitir notas fiscais, envie o certificado digital da empresa.
    </Alert>
  ) : null

  const isFetchingRef = useRef(isFetchingNextPage)
  isFetchingRef.current = isFetchingNextPage

  const handleRowsRendered = useCallback(
    (_visibleRows: { startIndex: number; stopIndex: number }) => {
      const flatLength = data?.pages.flatMap((p) => p.data).length ?? 0
      if (
        _visibleRows.stopIndex >= flatLength - LOAD_THRESHOLD &&
        hasNextPage &&
        !isFetchingRef.current
      ) {
        fetchNextPage()
      }
    },
    [data, hasNextPage, fetchNextPage],
  )

  const handleExpandChange = useCallback(
    (id: number) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setFiscalNoteExpanded(newExpanded ? id : undefined)
    },
    [],
  )

  if (isLoading) {
    return (
      <Stack spacing={1}>
        {certificateAlert}
        {Array.from(new Array(10)).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={COLLAPSED_ROW_HEIGHT} />
        ))}
      </Stack>
    )
  }

  if (isError || !data) {
    return (
      <Box>
        {certificateAlert}
        <Box textAlign="center">
          <ErrorOutline color="error" fontSize="large" />
          <Typography color="gray">
            Não foi possível carregar as notas fiscais.
          </Typography>
        </Box>
      </Box>
    )
  }

  const flatItems = data.pages.flatMap((page) => page.data)

  // if (flatItems.length === 0) {
  //   return (
  //     <Typography color="gray" textAlign="center" py={2}>
  //       Não encontramos nenhuma nota fiscal para o mês de{' '}
  //       {MonthsEnum[Number(month)]}
  //     </Typography>
  //   )
  // }

  const rowCount = hasNextPage ? flatItems.length + 1 : flatItems.length

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      overflow="hidden"
      position="relative"
    >
      {certificateAlert}

      {flatItems.length > 0 ? (
        <Box flexGrow={1} overflow="hidden">
          <List
            rowComponent={FiscalNoteRow}
            rowCount={rowCount}
            rowHeight={rowHeight}
            rowProps={{
              items: flatItems,
              expandedId: fiscalNoteExpanded,
              onExpandChange: handleExpandChange,
              hasNextPage: !!hasNextPage,
            }}
            onRowsRendered={handleRowsRendered}
            style={{ height: '100%', paddingBottom: 80, paddingTop: 2 }}
            overscanCount={5}
          />
        </Box>
      ) : (
        <Typography color="gray" textAlign="center" py={2}>
          {borrowerSearch
            ? `Não encontramos nenhuma nota fiscal desse tomador para o mês de ${MonthsEnum[Number(month)]}.`
            : `Não encontramos nenhuma nota fiscal para o mês de ${MonthsEnum[Number(month)]}.`}
        </Typography>
      )}

      {hideIssueButton ? null : (
        <Box display="flex" justifyContent="center">
          <Fab
            component={Link}
            to={routes.fiscalNotes.issue}
            variant="extended"
            size="large"
            color="primary"
            sx={{ position: 'absolute', bottom: 12 }}
          >
            Emitir nota fiscal
          </Fab>
        </Box>
      )}
    </Box>
  )
}
